import { useCallback } from "react";
import { useSetAtom } from "jotai";
import { useApi } from "./useApi";
import type {
    CreatePaymentMethodRequest,
    CreatePaymentMethodResponse,
} from "@models/paymentMethod";
import { isProcessingPaymentMethodAtom } from "@/store/paymentMethodAtoms";

const ORGANIZATIONS_ENDPOINT = "/organizations";

export const usePaymentMethodApi = () => {
    const { callApi } = useApi();
    const setIsProcessing = useSetAtom(isProcessingPaymentMethodAtom);

    // Capturing a card is what activates a newly approved membership (§1.6), so
    // this endpoint reports four distinct 402 codes — two of which
    // (activation_unavailable, payment_unavailable) the global
    // handlePaymentRequired toast predates and would mislabel "Payment declined". skipErrorNotification hands every failure
    // to the caller, which renders per-code inline copy instead.
    const createPaymentMethod = useCallback(
        async (
            organizationId: string,
            data: CreatePaymentMethodRequest,
        ): Promise<CreatePaymentMethodResponse> => {
            setIsProcessing(true);

            try {
                return await callApi<CreatePaymentMethodResponse>(
                    `${ORGANIZATIONS_ENDPOINT}/${organizationId}/payment-methods`,
                    {
                        method: "POST",
                        body: JSON.stringify(data),
                        skipErrorNotification: true,
                    },
                );
            } finally {
                setIsProcessing(false);
            }
        },
        [callApi, setIsProcessing],
    );

    return {
        createPaymentMethod,
    };
};
